import { useRef, useState, type PointerEvent } from 'react';
import { t } from '../i18n/index.ts';
import { Button, cx } from './ui.tsx';

// Signature with finger, pen or mouse. Stored as SVG path data in a 600×200 box: small,
// sharp when printed, and no image upload (the gateway accepts only M/L path commands).

const W = 600;
const H = 200;

export function SignaturePad({
  label,
  onChange,
  disabled,
}: {
  label: string;
  onChange(path: string | undefined): void;
  disabled?: boolean;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const drawing = useRef(false);
  // pointer events arrive faster than React re-renders: accumulate in a ref
  const current = useRef('');
  const [path, setPath] = useState('');

  const point = (e: PointerEvent<SVGSVGElement>) => {
    const box = svg.current!.getBoundingClientRect();
    const x = Math.round(((e.clientX - box.left) / box.width) * W);
    const y = Math.round(((e.clientY - box.top) / box.height) * H);
    return `${Math.min(W, Math.max(0, x))} ${Math.min(H, Math.max(0, y))}`;
  };

  const update = (next: string) => {
    current.current = next;
    setPath(next);
    // a single tap is no signature
    onChange(next.split('L').length > 3 ? next.trim() : undefined);
  };

  return (
    <div>
      <svg
        ref={svg}
        role="img"
        aria-label={label}
        viewBox={`0 0 ${W} ${H}`}
        className={cx(
          'block aspect-[3/1] w-full touch-none rounded-xl border border-dashed border-border bg-white',
          disabled ? 'opacity-50' : 'cursor-crosshair',
        )}
        onPointerDown={(e) => {
          if (disabled) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          update(`${current.current} M${point(e)}`);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          update(`${current.current} L${point(e)}`);
        }}
        onPointerUp={() => (drawing.current = false)}
        onPointerCancel={() => (drawing.current = false)}
      >
        <line x1="30" y1="160" x2="570" y2="160" stroke="#c7c7c7" strokeWidth="2" />
        <path
          d={path}
          fill="none"
          stroke="#111"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="mt-2 flex items-center justify-between gap-3 text-sm text-muted">
        <span>{t('protocol.signHere')}</span>
        <Button variant="ghost" disabled={disabled || !path} onClick={() => update('')}>
          {t('protocol.clear')}
        </Button>
      </div>
    </div>
  );
}

/** read-only rendering of a stored signature */
export function SignatureImage({ path, label }: { path: string; label: string }) {
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${W} ${H}`} className="block aspect-[3/1] w-full">
      <path d={path} fill="none" stroke="#111" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
